/**
 * LA NUIT COMMERCIALE, CÔTÉ NAVIGATEUR.
 *
 * `apps/web` n'a aucune dépendance interne — il ne peut donc pas importer
 * `businessNightDay()` de `@gestion/core`, ni Luxon. Cette copie existe pour
 * cette seule raison, et elle doit rester RIGOUREUSEMENT équivalente :
 * `tests/phase2-activity-soirees.test.ts` compare les deux implémentations
 * sur des instants réels, y compris de part et d'autre des changements
 * d'heure. Si l'une bouge sans l'autre, le test tombe.
 *
 * La règle est celle de toute la maison : avant 8 h du matin, on est encore
 * la veille. Une soirée annoncée « samedi 00 h 30 » appartient à la nuit du
 * VENDREDI.
 *
 * Le fuseau est traité par `Intl`, jamais par un décalage fixe : c'est ce qui
 * fait que le dernier dimanche d'octobre ne décale pas la nuit d'une heure.
 */

export const FUSEAU_METIER = 'Europe/Paris'
export const NIGHT_CUTOFF_HOUR = 8

const PARTIES = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU_METIER,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', hourCycle: 'h23',
})

/** Jour de la nuit commerciale d'un instant, en `YYYY-MM-DD`. */
export function businessNightDayISO(instantMs: number): string {
  const p: Record<string, string> = {}
  for (const part of PARTIES.formatToParts(new Date(instantMs))) p[part.type] = part.value
  const annee = Number(p['year'])
  const mois = Number(p['month'])
  const jour = Number(p['day'])
  const heure = Number(p['hour'])
  // Arithmétique de DATE, faite en UTC sur un triplet déjà localisé : aucun
  // risque de retomber sur un décalage horaire en soustrayant un jour.
  const base = Date.UTC(annee, mois - 1, jour)
  const ms = heure < NIGHT_CUTOFF_HOUR ? base - 86_400_000 : base
  return new Date(ms).toISOString().slice(0, 10)
}

export type PhaseSoiree = 'UPCOMING' | 'ONGOING' | 'FINISHED' | 'CANCELLED'

/**
 * FAUT-IL SITUER L'INSTANT COURANT SUR CET AXE ?
 *
 * Le repère ne dit PAS « la soirée est ouverte » — il dit « voici où se
 * trouve maintenant sur cet axe ». Une soirée prévue ce soir affiche déjà des
 * données réelles de la journée : lui refuser le repère parce qu'elle n'a pas
 * encore ouvert était une confusion entre l'état LIVE de l'événement et la
 * lecture du temps.
 *
 * D'où la règle : la soirée doit appartenir à la NUIT COMMERCIALE en cours.
 * Surtout pas une comparaison de dates civiles — samedi 03 h, la nuit en
 * cours est celle du vendredi, et la soirée du samedi soir (même date civile)
 * n'a rien à voir avec l'instant présent.
 *
 * Le reste — axe réellement temporel, instant dans les bornes tracées — est
 * décidé plus bas par `nowFraction()`, qui rend `null` plutôt que de coller
 * le repère à un bord.
 */
export function shouldShowCurrentTimeMarker(
  ev: { startsAt: string | null | undefined; phase: PhaseSoiree | string | null | undefined },
  nowMs: number,
): boolean {
  if (ev.phase === 'FINISHED' || ev.phase === 'CANCELLED') return false
  // Une soirée en cours EST la nuit en cours, par définition.
  if (ev.phase === 'ONGOING') return true
  if (!ev.startsAt) return false
  const debut = Date.parse(ev.startsAt)
  if (Number.isNaN(debut)) return false
  return businessNightDayISO(debut) === businessNightDayISO(nowMs)
}

/* ═════════════════════ LA SEMAINE D'EXPLOITATION ═══════════════════════ */

/**
 * Semaine LUNDI → DIMANCHE, appliquée aux nuits commerciales.
 *
 * Rendue en `YYYY-MM-DD` inclusifs. Calculée sur le jour de nuit déjà
 * localisé — jamais sur un `Date` UTC brut, qui ferait basculer la semaine
 * une heure trop tôt ou trop tard selon la saison.
 */
export function currentWeekBounds(nowMs: number): { from: string; to: string } {
  const jour = businessNightDayISO(nowMs)
  const [a, m, j] = jour.split('-').map(Number) as [number, number, number]
  const t = Date.UTC(a, m - 1, j)
  // getUTCDay : 0 = dimanche. On veut lundi = 0.
  const depuisLundi = (new Date(t).getUTCDay() + 6) % 7
  const lundi = t - depuisLundi * 86_400_000
  return {
    from: new Date(lundi).toISOString().slice(0, 10),
    to: new Date(lundi + 6 * 86_400_000).toISOString().slice(0, 10),
  }
}
